/**
 * W1a, scherm Koppelingen (opdracht Frank 22 okt, besluit 23 okt: een groot venster
 * vanuit het menu, geen route in App.js). Dit is wat het tabblad Ontdekken aanbiedt,
 * plus de twee pure keuzes die het venster maakt: wat past bij het zoekveld, en of een
 * adres al gekoppeld is.
 *
 * De eerste koppeling is Franks eigen Direkto-server. De omschrijving zegt alleen wat
 * de server zelf over zichzelf zegt; bestellen en betalen gebeurt bij de winkel.
 */
export const CATALOGUS = [
  {
    id: "direkto",
    naam: "Direkto",
    adres: "https://mcp.direkto.org/mcp",
    omschrijving: "Meerdere aangesloten webwinkels: producten zoeken, offertes en bestellinks. Bestellen en betalen doe je bij de winkel.",
  },
  // W4 (besluit Frank 24 oktober 2026): GitHub ALLEEN LEZEN, met een token dat Frank zelf
  // invult. Het adres eindigt op /readonly: de server van GitHub biedt dan alleen
  // leesgereedschap aan (docs/remote-server.md van github/github-mcp-server, gelezen op
  // 24 oktober). Het token gaat als Bearer mee, versleuteld opgeslagen, nooit teruggegeven.
  {
    id: "github",
    naam: "GitHub (alleen lezen)",
    adres: "https://api.githubcopilot.com/mcp/readonly",
    omschrijving: "Je repositories, issues en pull requests lezen. Alleen lezen: schrijven kan deze koppeling niet. Vul een eigen GitHub-token in.",
    sleutelNodig: true,
  },
];

/** Een adres zoals twee schrijfwijzen van hetzelfde adres gelijk uitkomen. PUUR. */
export function zelfdeAdres(a) {
  const tekst = String(a || "").trim();
  try {
    const u = new URL(tekst);
    // `URL` zet de host al in kleine letters; alleen de slash achteraan gaat eraf.
    return `${u.protocol}//${u.host}${u.pathname.replace(/\/+$/, "")}${u.search}`;
  } catch {
    return tekst.replace(/\/+$/, "").toLowerCase();
  }
}

/** Staat dit adres al bij de actieve koppelingen? Een ontkoppelde telt niet. PUUR. */
export function alGekoppeld(adres, servers) {
  const doel = zelfdeAdres(adres);
  return (servers || []).some((s) => s.actief !== false && zelfdeAdres(s.adres) === doel);
}

/**
 * Wat bij het zoekveld past: hoofdletterongevoelig op elk genoemd veld, en een leeg
 * veld geeft alles terug, niet niets. `velden` zijn functies, zodat een vertaalde
 * omschrijving meetelt in de taal die de persoon leest. PUUR.
 */
export function zoekIn(lijst, zoek, velden) {
  const z = String(zoek || "").trim().toLowerCase();
  if (!z) return [...(lijst || [])];
  return (lijst || []).filter((item) => velden.some((veld) => String(veld(item) || "").toLowerCase().includes(z)));
}

/**
 * CB1 (GO Frank 4 november 2026): de lijst komt uit de backend (`GET /mcp-servers/catalogus`), een bron voor app en
 * PWA. Alleen een antwoord met de eigen vorm `{ items: [...] }` telt; een ander antwoord (een oudere backend, een
 * fout) geeft `null`, en dan blijft het scherm bij `CATALOGUS` hierboven. PUUR.
 */
export function uitBackend(data, taal = "nl") {
  if (!data || !Array.isArray(data.items) || !data.items.length) return null;
  // Glasronde 0.9.243 punt 1b: een dienst met eigen adres heeft geen vast adres maar een pad.
  const goed = data.items.filter((i) => i && i.id && i.naam && (/^https:\/\//.test(String(i.adres || ""))
    || (i.soort === EIGEN_ADRES && /^\//.test(String(i.pad || "")))
    // Glasronde 0.9.244: niet koppelbaar staat er met de reden, zonder adres en zonder knop.
    || (i.soort === NIET_KOPPELBAAR && i.reden)));
  if (!goed.length) return null;
  return goed.map((i) => ({
    id: i.id, naam: i.naam, adres: i.adres,
    omschrijving: taal === "en" && i.omschrijving_en ? i.omschrijving_en : i.omschrijving,
    sleutelNodig: i.inloggen === "sleutel", inloggen: i.inloggen || "onbekend", rechten: i.rechten || "onbekend",
    zeker: i.zeker !== false, reden: i.reden || "", bron: i.bron || "",
    soort: [EIGEN_ADRES, NIET_KOPPELBAAR].includes(i.soort) ? i.soort : VAST, pad: i.pad || "",
  }));
}

export const ALLE = "alle";
export const VAST = "vast";
export const EIGEN_ADRES = "eigen_adres";
export const NIET_KOPPELBAAR = "niet_koppelbaar";

/**
 * Glasronde 0.9.243 punt 1b: het adres van een dienst die je zelf draait (n8n). Wat de persoon intikt is het adres
 * van de eigen server; het pad komt erachter. Wie het hele adres plakt, krijgt het pad niet dubbel. Zonder schema
 * wordt het https, net als in de backend (`normaliseer_adres`), die het daarna nog steeds zelf keurt. PUUR.
 */
export function eigenAdres(server, pad) {
  let tekst = String(server || "").trim().replace(/\/+$/, "");
  if (!tekst) return "";
  if (!/^[a-z]+:\/\//i.test(tekst)) tekst = `https://${tekst}`;
  if (pad && tekst.toLowerCase().endsWith(pad.toLowerCase())) tekst = tekst.slice(0, -pad.length).replace(/\/+$/, "");
  return `${tekst}${pad || ""}`;
}

/** Is dit catalogusitem al gekoppeld? Bij een eigen adres: een actieve koppeling die op het pad eindigt. PUUR. */
export function itemGekoppeld(item, servers) {
  if (item.soort === NIET_KOPPELBAAR) return false;
  if (item.soort !== EIGEN_ADRES) return alGekoppeld(item.adres, servers);
  const pad = zelfdeAdres(`https://x${item.pad}`).replace("https://x", "");
  return (servers || []).some((s) => s.actief !== false && zelfdeAdres(s.adres).endsWith(pad));
}

/**
 * Glasronde 0.9.243 punt 1a: de koppelingen die NovaMund zelf meebrengt. Die staan niet in de MCP-catalogus, want ze
 * koppel je in Instellingen, onder Verbindingen; Ontdekken toont ze met hun status zodat ze niet "ontbreken".
 * `bron` is de sleutel in `GET /connections`; Direkto is een MCP-server en wordt op zijn adres herkend.
 */
export const INGEBOUWD = [
  { id: "gmail", naam: "Gmail", bron: "gmail" },
  { id: "google_calendar", naam: "Google Calendar", bron: "google_calendar" },
  { id: "google_drive", naam: "Google Drive", bron: "google_drive" },
  { id: "fathom", naam: "Fathom", bron: "fathom" },
  { id: "tasq", naam: "TASQ", bron: "tasq" },
  { id: "nextcloud", naam: "Nextcloud", bron: "nextcloud" },
  { id: "direkto", naam: "Direkto", adres: "https://mcp.direkto.org/mcp" },
];
export const GEKOPPELD = "gekoppeld";
export const VERLOPEN = "verlopen";
export const NIET_GEKOPPELD = "niet_gekoppeld";

/** De status per ingebouwde koppeling: gekoppeld, verlopen of niet gekoppeld. Een ingetrokken koppeling telt niet. PUUR. */
export function ingebouwdeStatus(koppelingen, servers) {
  return INGEBOUWD.map((i) => {
    if (i.adres) return { ...i, status: alGekoppeld(i.adres, servers) ? GEKOPPELD : NIET_GEKOPPELD };
    const actief = (koppelingen || []).filter((k) => k.bron === i.bron && k.actief !== false);
    const status = !actief.length ? NIET_GEKOPPELD : actief.some((k) => !k.verlopen) ? GEKOPPELD : VERLOPEN;
    return { ...i, status };
  });
}

/** CB1: filteren op inloggen, rechten en zekerheid; `alle` laat dat filter los. Een oude regel zonder velden telt als onbekend. PUUR. */
export function filterOp(lijst, { inloggen = ALLE, rechten = ALLE, zeker = ALLE, soort = ALLE } = {}) {
  return (lijst || []).filter((i) =>
    (soort === ALLE || (i.soort || VAST) === soort) &&
    (inloggen === ALLE || (i.inloggen || (i.sleutelNodig ? "sleutel" : "onbekend")) === inloggen)
    && (rechten === ALLE || (i.rechten || "onbekend") === rechten)
    && (zeker === ALLE || (zeker === "zeker") === (i.zeker !== false)));
}
